<template>
  <k-modal id="add-new-evidence-modal" :show="showModal"
    @close="onClose"
    :closeOnClickAway="false"
  >
  <template #header>
    <h3>{{ modalTitle }}</h3>
  </template>
  <template #body>
    <div class="modal-body">
      <k-form :columns="10">
        <k-form-section :column-span="10">
          <k-form-field label="Evidence Name" :required="true" field-id="project-name-input">
            <input id="project-name-input" class="form-control" :readonly="editingDisabled" placeholder="Type Evidence name here..." v-model="evidenceName">
          </k-form-field>

          <k-form-field label="Evidence Description" :required="true" field-id="project-description-input">
            <textarea
              id="project-description-input"
              placeholder="Type description here..."
              v-model="evidenceDescription"
              rows="3"
              class="form-control"
              :maxlength="descMaxLength"
              :readonly="editingDisabled"
            />
            <span class="character-count" :class="{ 'near-limit': evidenceDescription.length > descMaxLength * 0.9 }">
              {{ evidenceDescription.length }}/{{ descMaxLength }} characters
            </span>
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10">
          <k-form-field label="Add Evidence" :required="true">
            <div class="evidence-upload-container">
              <div class="evidence-mode-toggle">
                <button
                  type="button"
                  class="btn btn-primary toggle-btn"
                  :class="{ active: isLinkMode }"
                  @click="setEvidenceMode('link')"
                  :disabled="editingDisabled"
                  title="Use external link as evidence"
                  aria-label="Use external link as evidence"
                >
                  Link
                </button>
                <button
                  type="button"
                  class="btn btn-primary toggle-btn"
                  :class="{ active: !isLinkMode }"
                  @click="setEvidenceMode('file')"
                  :disabled="editingDisabled"
                  title="Upload file as evidence"
                  aria-label="Upload file as evidence"
                >
                  Upload
                </button>
              </div>
              <div v-if="isLinkMode" class="evidence-link-wrapper">
                <p for="project-evidence-input">You can also upload a file. Switch using the above buttons.</p>
                <span class="helper-text">Please save your evidence link before submitting the form</span>
                <div v-if="!isEditingUrl" class="evidence-link-view">
                  <div class="scope-link-container">
                    <a :href="evidence.url" target="_blank">{{ evidence.url }}</a>
                  </div>
                  <button class="btn btn-outlined" @click="isEditingUrl = true" title="Edit evidence resource link" aria-label="Edit evidence resource link">
                  <i class="fas fa-pencil-alt"></i>
                  </button>
                </div>
                <div v-else class="evidence-link-edit">
                  <input class="form-control" v-model="evidence.url" :readonly="editingDisabled" placeholder="Add evidence resource link here..." />
                  <button class="btn btn-success" @click="setUrlEvidence(evidence.url)" :disabled="!evidence.url.trim()" title="Save evidence resource link" aria-label="Save evidence resource link">
                    <i class="fas fa-check"></i>
                  </button>
                </div>
              </div>
              <div v-else class="evidence-upload-wrapper">
                <p for="project-evidence-input">You can also submit an external link as an evidence. Switch using the above buttons.</p>
                <span v-if="!evidence.file && evidenceData?.signed_url && modalMode !== 'create'" class="upload-replacement-text">
                  Upload a new file to replace the current one
                </span>
                <dropfield
                  @file="setFileEvidence($event[0])"
                  id="upload-evidence-document"
                  :immediatePost="false"
                  :enablePreviews="true"
                  ref="evidenceUploadField"
                  :disableDropfield="editingDisabled"
                  :customMessage="dropfieldMessage"
                  />
              </div>
            </div>
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10">
          <label class="label-text">Select Competencies relevant to the evidence <span class="form-required">*</span></label>
            <template v-if="projectCompetenciesReady && projectCompetencies.length > 0">
              <k-editable-table
                v-model="projectCompetencies"
                :headers="tableHeaders"
                :max="10"
              />
            </template>
            <div v-else>
              <span class="label-text">No competencies found for this project</span>
            </div>
        </k-form-section>
      </k-form>
    </div>
  </template>
  <template #footer>
    <div class="modal-footer-left">
      <button class="btn btn-outlined" @click="$emit('close')" aria-label='Cancel'>Cancel</button>
    </div>
    <div class="modal-footer-right">
      <button class="btn btn-success submit" @click="saveNewEvidence" aria-label='Submit for review' :disabled="!validSubmitForReview || editingDisabled">Submit for Review</button>
    </div>
  </template>
  </k-modal>
</template>

<style>
#add-new-evidence-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-evidence-modal .modal-container {
  width: 80vw;
}

#add-new-evidence-modal .form-control[readonly],
#add-new-evidence-modal textarea[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
  cursor: not-allowed;
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.evidence-upload-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.evidence-mode-toggle {
  display: flex;
  gap: 0;
  margin: 15px 0;
}

.evidence-mode-toggle .toggle-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-right: 1px solid var(--input-border);
  margin: 0;
  min-width: 120px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: var(--kate-button-primary);
  color: var(--kate-type-dark);
  border-color: var(--kate-button-primary);
}

.evidence-mode-toggle .toggle-btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.evidence-mode-toggle .toggle-btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.evidence-mode-toggle .toggle-btn:last-child {
  border-right: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.evidence-mode-toggle .toggle-btn:hover:not(:disabled):not(.active) {
  background-color: var(--kate-button-primary-hover);
  color: var(--kate-type-light);
}

.evidence-mode-toggle .toggle-btn.active {
  background-color: var(--kate-background);
  border-color: var(--kate-background);
  color: var(--kate-type-white);
}

.evidence-mode-toggle .toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.evidence-link-wrapper,
.evidence-upload-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
}

.evidence-link-wrapper > label,
.evidence-upload-wrapper > label {
  font-weight: 600;
  color: var(--kate-type-primary);
  margin-bottom: 5px;
}

.evidence-upload-container > span {
  align-self: center;
  font-weight: bold;
  margin-top: 20px;
}

.evidence-link-view,
.evidence-link-edit {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
}

.scope-link-container {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  background-color: var(--input-background);
  font-family: inherit;
  line-height: 1.5;
  min-height: 38px;
  display: flex;
  align-items: center;
}

.evidence-upload-container :deep(.k-dropbox) {
  display: flex;
  min-height: 50px;
  height: 50px;
}

.evidence-upload-container :deep(.k-dropbox .upload-text) {
  font-weight: 500;
  color: var(--kate-type-secondary);
}

.evidence-upload-container :deep(.k-dropbox i) {
  margin-top: 12px;
  font-size: 20px;
  color: var(--kate-type-primary);
}

#upload-evidence-document {
  width: 100%;
  border: 2px dashed var(--input-border);
  border-radius: 8px;
  transition: border-color 0.2s ease;
}

#upload-evidence-document:hover {
  border-color: var(--kate-button-primary);
}

.evidence-upload-container :deep(.k-dropbox) > label {
  display: block;
  padding: 0;
  font-size: 1em;
}

.character-count {
  float: right;
  font-size: 0.8em;
  color: var(--kate-type-secondary);
  margin-top: 4px;
}

.character-count.near-limit {
  color: var(--kate-type-danger);
}

.helper-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  margin: 0;
  font-style: italic;
}

.upload-replacement-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  font-style: italic;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import { KForm, KFormField, KFormSection } from '@base-components/forms';
import KEditableTable from '@base-components/k-editable-table.vue';
import Dropfield from '@base-components/dropfield.vue';

export default {
  components: {
    KModal,
    KEditableTable,
    Dropfield,
    KForm,
    KFormField,
    KFormSection,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    modalMode: {
      type: String,
      required: true,
    },
    evidenceData: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      descMaxLength: 2048,
      isUploading: false,
      isUpdating: false,
      evidenceName: '',
      evidenceDescription: '',
      projectCompetencies: [],
      projectCompetenciesReady: false,
      evidenceId: null,

      evidence: {
        type: 'file', // by default link otherwise some kind of type
        file: null, // File object or null
        url: '', // URL string
        path: null, // uploaded file path
      },
      isEditingUrl: true,
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getProjectCompetencies();
      }
    },

    isUpdating(newVal) {
      if (newVal) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },

    evidenceData: {
      handler(newVal) {
        if (newVal) {
          this.evidenceName = newVal.name || '';
          this.evidenceDescription = newVal.description || '';
          this.evidenceId = newVal.evidenceId;

          if (newVal.signed_url && newVal.signed_url.trim() !== '') {
            // File evidence (for all the file types)
            this.evidence = {
              type: 'file',
              file: null,
              url: '',
              path: newVal.evidence_link,
            };
            this.isEditingUrl = false;
          } else {
            // URL evidence (only links)
            this.evidence = {
              type: 'link',
              file: null,
              url: newVal.evidence_link || '',
              path: null,
            };
            this.isEditingUrl = false;
          }
        } else {
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    evidenceInputMode() {
      return this.evidence.type;
    },

    isLinkMode() {
      return this.evidenceInputMode === 'link';
    },

    evidenceType() {
      // allowed types ["document", "link", "file", "video", "image", "pdf"],
      if (this.evidence.file) {
        const mimeType = this.evidence.file.type;
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType === 'application/pdf') return 'pdf';
        if (mimeType.includes('document') || mimeType.includes('word')) return 'document';
        return 'file';
      }
      return 'link';
    },

    evidenceLink() {
      return this.evidence.file ? this.evidence.path : this.evidence.url;
    },

    hasValidEvidence() {
      return this.evidence.file || this.evidence.url.trim();
    },

    modalTitle() {
      if (this.modalMode === 'view' && this.evidenceData) {
        return `View ${this.evidenceData.name}`;
      }
      return this.modalMode === 'edit' && this.evidenceData ? `Edit ${this.evidenceData.name}` : 'Add New Evidence';
    },

    validName() {
      return this.evidenceName.trim() !== '';
    },

    validDescription() {
      return this.evidenceDescription.trim() !== '';
    },

    validCompetencies() {
      return this.listSelectedCompetencies().length > 0;
    },

    validSubmitForReview() {
      return Boolean(this.validName && this.validDescription && this.validCompetencies && this.hasValidEvidence);
    },

    editingDisabled() {
      return this.modalMode === 'view' || this.evidenceData?.approved || this.isUpdating;
    },

    dropfieldMessage() {
      return `<i class="fas fa-cloud-upload-alt"></i>
      <span class="upload-text">Click or drag evidence file here to upload</span>`;
    },

    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: !this.editingDisabled,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },
  },

  methods: {
    resetFileField() {
      if (this.$refs.evidenceUploadField) {
        this.$refs.evidenceUploadField.reset();
      }
    },

    setFileEvidence(file) {
      this.evidence = {
        type: 'file',
        file,
        url: '',
        path: null,
      };
      this.isEditingUrl = false;
    },

    setUrlEvidence(url) {
      this.evidence = {
        type: 'link',
        file: null,
        url,
        path: null,
      };
      this.isEditingUrl = false;
      this.resetFileField();
    },

    clearEvidence() {
      this.evidence = {
        type: this.evidence.type,
        file: null,
        url: '',
        path: null,
      };
      this.isEditingUrl = true;
      this.resetFileField();
    },

    getProjectCompetencies() {
      this.projectCompetenciesReady = false;
      this.$Loading.start();
      this.$logger.info('Fetching all competencies for the project', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}/competencies`)
        .then(response => {
          this.$logger.info('Succefully fetched all competencies for the project', this.projectId);
          this.projectCompetencies = response.data || [];
          // if in view mode / edit mode
          this.markSelectedComeptencies();
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching competencies for project', this.projectId, err);
          this.$ktoast.warning('Error fetching competencies');
          this.projectCompetencies = [];
        }).then(() => {
          this.projectCompetenciesReady = true;
          this.$Loading.finish();
        });
    },

    listSelectedCompetencies() {
      return this.projectCompetencies.filter(competency => competency.checkbox);
    },

    async saveNewEvidence() {
      try {
        this.isUpdating = true;
        let evidenceId;
        if (this.modalMode === 'create') {
          const evidencePayload = {
            programme_id: this.programmeId,
            name: this.evidenceName,
            description: this.evidenceDescription,
            project_competency_mapping_ids: this.listSelectedCompetencies().map(competency => competency.id),
          };
          this.$logger.info('Creating project evidence entry');
          const response = await this.$http.post(`/api/curriculum/programmes/projects/${this.projectId}/evidence`, evidencePayload);
          this.$ktoast.success('Project evidence entry created successfully', response.data);
          evidenceId = response.data.project_evidence_id;
        } else {
          evidenceId = this.evidenceId;
          this.$logger.info('Updating existing project evidence', { evidenceId });
        }

        // check if it's file
        if (this.evidence.file) {
          await this.uploadEvidenceFile(evidenceId);
        }
        await this.createEvidenceVersion(evidenceId);
        this.$logger.info('Project evidence version created successfully');
        this.$ktoast.success('Project evidence submitted for review');
        this.$emit('close');
        this.$emit('update');
        this.clearFields();
      } catch (err) {
        this.$logger.autowarn('Error saving project evidence', err);
        this.$ktoast.warning('Error submitting project evidence for review');
      } finally {
        this.isUpdating = false;
      }
    },

    async createEvidenceVersion(evidenceId) {
      const versionPayload = {
        programme_id: this.programmeId,
        name: this.evidenceName,
        description: this.evidenceDescription,
        evidence_type: this.evidenceType,
        evidence_link: this.evidenceLink,
        feedback_text: null,
        project_competency_mapping_ids: this.listSelectedCompetencies().map(competency => competency.id),
      };
      this.$logger.info('Creating project evidence version', { evidenceId, payload: versionPayload });
      await this.$http.post(`/api/curriculum/programmes/projects/${this.projectId}/evidence/${evidenceId}/versions`, versionPayload);
    },

    async uploadEvidenceFile(evidenceId) {
      if (!this.evidence.file) return;
      try {
        this.isUploading = true;
        const uploadUrlPayload = {
          file_name: this.evidence.file.name,
          file_size: this.evidence.file.size,
          content_type: this.evidence.file.type,
        };
        this.$logger.info('Getting upload URL for evidence file');
        const uploadResponse = await this.$http.post(
          `/api/curriculum/programmes/projects/${this.projectId}/evidence/${evidenceId}/upload-url?programme_id=${this.programmeId}`,
          uploadUrlPayload,
        );

        const { upload_url: uploadUrl, file_path: filepPath } = uploadResponse.data;
        this.evidence.path = filepPath;

        this.$logger.info('Uploading file to signed URL');
        await this.$http.put(uploadUrl, this.evidence.file);

        this.$logger.info('File uploaded successfully', { filePath: filepPath });
      } catch (err) {
        this.$logger.autowarn('Error uploading evidence file', err);
        throw err;
      } finally {
        this.isUploading = false;
      }
    },

    clearFields() {
      this.evidenceName = '';
      this.evidenceDescription = '';
      this.projectCompetencies = [];
      this.evidenceId = null;
      this.clearEvidence();
    },

    editEvidenceLink() {
      this.displayEvidenceDoc = false;
      this.pendingProjectEvidenceDocument = this.projectEvidenceDocument;
    },
    onClose() {
      this.clearFields();
      this.$emit('close');
    },

    markSelectedComeptencies() {
      if (!this.evidenceData || !this.evidenceData.competency_mappings) return;
      const selectedCompetencyIds = this.evidenceData.competency_mappings.map(mapping => mapping.project_competency_mapping_id);
      this.projectCompetencies.forEach((competency, index) => {
        this.projectCompetencies[index].checkbox = selectedCompetencyIds.includes(competency.id);
      });
    },

    setEvidenceMode(mode) {
      this.clearEvidence();
      this.evidence.type = mode;
    },
  },
};
</script>
